import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import TruncateLines from '@components/TruncateLines'
import CancelIcon from '@mui/icons-material/Cancel'
import { AutocompleteRenderGroupParams, AutocompleteRenderInputParams, Divider, InputAdornment, Typography } from '@mui/material'
import { colors } from '@theme/variables'

import { CancelSearchIconWrapper, GroupItems, SearchInput, StyledAutocomplete } from './index.styles'

interface AutocompleteOption {
  label: string
  type: string
  optionLabel: string[]
  matchedStrings: string[]
}

enum AutocompleteGroups {
  company = 'company',
  term = 'term',
}

interface SearchAutocompleteProps {
  terms?: string[]
  companies?: string[]
  localSearchStr: string
  onSelectPredictiveSearch: (value: string) => void
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyPress: (e: React.KeyboardEvent) => void
  onClear: () => void
}

const SearchAutocomplete = ({
  terms,
  companies,
  localSearchStr,
  onSelectPredictiveSearch,
  onSearch,
  onKeyPress,
  onClear,
}: SearchAutocompleteProps) => {
  const { t } = useTranslation()

  const preparedSearchValue = useMemo(
    () => `(${localSearchStr.replaceAll(/\W/g, (value: string) => (value === ' ' ? '|' : `\\${value}`))})`,
    [localSearchStr],
  )

  const preparedTerms = useMemo(
    () =>
      terms?.map(term => ({
        label: term,
        type: AutocompleteGroups.term,
        optionLabel: term.replaceAll(new RegExp(preparedSearchValue, 'ig'), localSearchStr).split(localSearchStr),
        matchedStrings: term.match(new RegExp(preparedSearchValue, 'ig')),
      })) ?? [],
    [terms, preparedSearchValue],
  )
  const preparedCompanies = useMemo(
    () =>
      companies?.map(company => ({
        label: company,
        type: AutocompleteGroups.company,
        optionLabel: company.replaceAll(new RegExp(preparedSearchValue, 'ig'), localSearchStr).split(localSearchStr),
        matchedStrings: company.match(new RegExp(preparedSearchValue, 'ig')),
      })) ?? [],
    [companies, preparedSearchValue],
  )

  return (
    <StyledAutocomplete<{ label: string; type: string }>
      id="search"
      options={[...preparedTerms, ...preparedCompanies]}
      freeSolo
      disableClearable
      inputValue={localSearchStr}
      ListboxProps={{ style: { maxHeight: '350px' } }}
      groupBy={(option: AutocompleteOption) => option.type}
      renderOption={(optionProps: Event, option: AutocompleteOption) => (
        // eslint-disable-next-line
        <li {...optionProps}>
          <TruncateLines lines={1} lineHeight={1}>
            {option.optionLabel.map((label, index) => (
              <>
                <b>{label}</b>
                {option.matchedStrings?.[index] ?? null}
              </>
            ))}
          </TruncateLines>
        </li>
      )}
      filterOptions={(options: AutocompleteOption[]) => options}
      onChange={(_: Event, option: AutocompleteOption) => onSelectPredictiveSearch(option.label)}
      renderGroup={(params: AutocompleteRenderGroupParams) => (
        <>
          {params.group === AutocompleteGroups.company ? (
            <>
              <Typography variant="subtitle1" paddingLeft="16px" height="30px" fontWeight="700" lineHeight="30px">
                {t(translationKeys.SEARCH_FILTER_BY_COMPANIES)}
              </Typography>
              <Divider />
            </>
          ) : null}
          <GroupItems>{params.children}</GroupItems>
        </>
      )}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <SearchInput
          // eslint-disable-next-line
          {...params}
          value={localSearchStr}
          variant="outlined"
          type="text"
          onChange={onSearch}
          onKeyPress={onKeyPress}
          inputProps={{
            ...params.inputProps,
            'data-testid': 'input_search',
          }}
          // eslint-disable-next-line
          InputProps={{
            ...params.InputProps,
            className: '',
            endAdornment: (
              <InputAdornment position="end">
                <CancelSearchIconWrapper onClick={onClear} data-testid="reset_search_icon">
                  <CancelIcon htmlColor={colors.lightGrey} />
                </CancelSearchIconWrapper>
              </InputAdornment>
            ),
          }}
          label=""
          autoFocus
          fullWidth
        />
      )}
    />
  )
}

export default SearchAutocomplete
